import { BUILD_PROPERTIES } from '@/editor/constant/buildProperties';
import { EDoorWindowType, IWindowData } from '@/editor/data/model/model';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { globalStores } from '@/editor/stores';
import { EEntityType } from '@/editor/types';
import {
  EParamGroupLabel,
  EParamLabel,
  EParamType,
  EParamUnit,
  EParamsKeys,
  IEditParams
} from '@/editor/types/editParamType';
import { IMaterialInfoData } from '@/pages/app/layout/tab-params/types';
import { getLibraryModelBaseInfo, getTransformingData } from '../utils';

function getDoorWindowGroupName(windowData: IWindowData) {
  switch (windowData.type) {
    case EDoorWindowType.singleDoor:
      return EParamGroupLabel.SINGLEDOOR;
    case EDoorWindowType.doubleDoor:
      return EParamGroupLabel.DOUBLEDOOR;
    case EDoorWindowType.motherDoor:
      return EParamGroupLabel.MOTHERWINDOW;
    case EDoorWindowType.frenchWindow:
      return EParamGroupLabel.FRENCH_WINDOW;
    case EDoorWindowType.window:
      return EParamGroupLabel.WINDOW;
    default:
      console.warn('未知的门窗类型', windowData.type);
      return EParamGroupLabel.EMPTY_LABEL;
  }
}

export function getDoorWindowLimitInfo(doorWindowData: IWindowData) {
  /** 获取墙体信息 */
  let wallLength: number = 0;
  const { relativeWallId, wallType } = doorWindowData.relativeWallInfo;
  const currentWall =
    wallType === EEntityType.Wall
      ? globalStores.wallStore.getWallData(relativeWallId)
      : globalStores.separateWallStore.getSepData(relativeWallId, false);
  if (currentWall) {
    const isWall = 'baseData' in currentWall;
    const start = isWall ? currentWall.baseData.start : currentWall.start;
    const end = isWall ? currentWall.baseData.end : currentWall.end;
    const currentWallLength = Math.round(Point2dUtils.distanceTo(start, end));
    wallLength = !isNaN(currentWallLength) ? currentWallLength : 0;
  }

  const limitHeight = globalStores.paramStore.layerHeight;

  return {
    limitWidth: wallLength ?? undefined,
    limitHeight
  };
}

export function getDoorWindowEntityParamInfo(entityId: string) {
  const doorWindowData =
    getTransformingData<IWindowData>(entityId, EEntityType.DoorWindow) ||
    globalStores.modelStore.getWindowData(entityId);
  if (doorWindowData) {
    const modelInfo = globalStores.modelStore.getModel(doorWindowData.modelId);
    const noSiblingModel = globalStores.modelStore.getModelsByType(doorWindowData.type).length <= 1;

    if (!modelInfo) return undefined;

    const width = Math.round(doorWindowData.width);
    const height = Math.round(doorWindowData.height);
    const offGround = doorWindowData.offGround;

    const { limitWidth, limitHeight } = getDoorWindowLimitInfo(doorWindowData);

    const paramDatas: IEditParams = {
      id: entityId,
      type: EEntityType.DoorWindow,
      title: getDoorWindowGroupName(doorWindowData),
      group: [
        ...(noSiblingModel
          ? []
          : [
              {
                id: EParamGroupLabel.EMPTY_LABEL,
                label: EParamGroupLabel.EMPTY_LABEL,
                fields: [EParamsKeys.doorWindowInfo],
                hideLabel: true
              }
            ]),
        {
          id: EParamGroupLabel.SIZE,
          label: EParamGroupLabel.SIZE,
          linkedFields: [EParamsKeys.width, EParamsKeys.height],
          defaultLinked: false,
          fields: [EParamsKeys.width, EParamsKeys.height, EParamsKeys.offGround],
          resettable: true
        }
      ],
      values: {
        [EParamsKeys.width]: width,
        [EParamsKeys.height]: height,
        [EParamsKeys.offGround]: offGround,
        [EParamsKeys.doorWindowInfo]: {
          ...getLibraryModelBaseInfo(modelInfo),
          id: doorWindowData.id,
          type: EEntityType.DoorWindow
        } satisfies IMaterialInfoData
      },
      fields: {
        [EParamsKeys.width]: {
          type: EParamType.NUMBER,
          unit: EParamUnit.MM,
          label: EParamLabel.MODEL_WIDTH,
          settings: {
            readonly: false,
            max: limitWidth,
            min: BUILD_PROPERTIES.MIN_INPUT
          }
        },
        [EParamsKeys.height]: {
          type: EParamType.NUMBER,
          label: EParamLabel.MODEL_HEIGHT,
          unit: EParamUnit.MM,
          settings: {
            readonly: false,
            max: limitHeight,
            min: BUILD_PROPERTIES.MIN_INPUT
          }
        },
        [EParamsKeys.offGround]: {
          label: EParamLabel.MODEL_OFFGROUND,
          type: EParamType.NUMBER,
          unit: EParamUnit.MM,
          settings: {
            readonly: false,
            max: globalStores.paramStore.layerHeight,
            min: 0
          }
        },
        [EParamsKeys.doorWindowInfo]: {
          type: EParamType.MATERIAL_INFO,
          label: EParamLabel.ROOM_TYPE,
          settings: {
            hideLabel: true,
            readonly: false
          }
        }
      }
    };
    return paramDatas;
  }

  return undefined;
}
